import { type ReactElement, useEffect } from 'react';
import { useFetcher } from 'react-router';
import { useTranslation } from 'react-i18next';
import { FormSubmitButton } from '@/components/buttons/form-submit-button';
import { Mail } from 'lucide-react';
import type { AuthorizePasswordlessEmailResponse } from '@/routes/action.authorize-passwordless-email';
import { cn } from '@/lib/utils';
import AuthStepShell from '../auth-step-shell';
import { useLoginFlowVariant } from '../variant';

interface SendCodeStepProps {
    email: string;
    onEditEmail: () => void;
    onBack: () => void;
    onCodeSent: () => void;
}

export default function SendCodeStep({ email, onEditEmail, onBack, onCodeSent }: SendCodeStepProps): ReactElement {
    const { t } = useTranslation('login');
    const fetcher = useFetcher<AuthorizePasswordlessEmailResponse>();
    const isPage = useLoginFlowVariant() === 'page';

    const error = fetcher.state === 'idle' && fetcher.data?.success === false ? fetcher.data.error : undefined;

    useEffect(() => {
        if (fetcher.state !== 'idle' || !fetcher.data) return;
        if (fetcher.data.success) {
            onCodeSent();
        } else if (fetcher.data.requiresLogin) {
            onBack();
        }
    }, [fetcher.data, fetcher.state, onCodeSent, onBack]);

    return (
        <AuthStepShell
            heading={t('sheet.chooseCodeDelivery', {
                defaultValue: 'Choose how you would like to receive the code',
            })}
            onBack={onBack}
            step={2}>
            <div className="mt-6 flex gap-3">
                <span>{email}</span>
                <button
                    type="button"
                    className="cursor-pointer font-medium text-foreground underline underline-offset-2"
                    onClick={onEditEmail}>
                    {t('sheet.edit', { defaultValue: 'Edit' })}
                </button>
            </div>

            <div className="mt-6">
                <div className="flex items-center gap-4 rounded-xs border-2 border-foreground bg-secondary p-4">
                    <Mail className="size-5 shrink-0 text-muted-foreground" />
                    <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-foreground">
                            {t('sheet.emailOption', { defaultValue: 'Email' })}
                        </p>
                        <p className="truncate text-xs text-muted-foreground">{email}</p>
                    </div>
                </div>
            </div>

            {error ? (
                <div className="mt-4 rounded bg-destructive/10 border border-destructive/20 px-4 py-3 text-sm text-destructive">
                    {error.code === 'RATE_LIMITED'
                        ? t('sheet.otpRateLimitError', { defaultValue: 'Too many attempts. Please wait a few minutes.' })
                        : t('sheet.otpSendError', { defaultValue: 'Unable to send code. Please try again.' })}
                </div>
            ) : null}

            <fetcher.Form method="post" action="/action/authorize-passwordless-email" className="mt-6">
                <input type="hidden" name="email" value={email} />
                <FormSubmitButton
                    defaultText={t('sheet.sendCode', { defaultValue: 'Send Code' })}
                    submittingText={t('sheet.sendingCode', { defaultValue: 'Sending...' })}
                    fetcher={fetcher}
                    className={cn('w-full', isPage && 'h-12 text-base sm:h-14')}
                />
            </fetcher.Form>

            <div className="mt-6 text-center">
                <button
                    type="button"
                    className="cursor-pointer text-sm text-foreground underline underline-offset-2"
                    onClick={onBack}>
                    {t('sheet.tryAnotherWay', { defaultValue: 'Try another way to sign in' })}
                </button>
            </div>
        </AuthStepShell>
    );
}
